import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addBlog, editBlog } from './feature/blog.slice';
import { useToast } from './context/ToastContext'; // Make sure this path is correct

const Create = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [author, setAuthor] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const blogToEdit = useSelector((state) => 
    id ? state.blog.blogs.find(blog => String(blog.id) === String(id)) : null
  );

  useEffect(() => {
    if (isEditMode && blogToEdit) {
      setTitle(blogToEdit.title);
      setBody(blogToEdit.body);
      setAuthor(blogToEdit.author);
    } else if (isEditMode && !blogToEdit) {
        navigate('/'); // Redirect if blog not found
        showToast('Blog not found', 'error');
    }
  }, [isEditMode, blogToEdit, navigate, showToast]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const blogData = { title, body, author };

    // Simulate network delay for better UX feel
    setTimeout(() => {
        if (isEditMode) {
            dispatch(editBlog({ ...blogData, id }));
            showToast('Blog updated successfully!', 'success');
            navigate(`/blogs/${id}`);
        } else {
            const newBlog = { ...blogData, id: Date.now() };
            dispatch(addBlog(newBlog));
            showToast('New blog added!', 'success');
            navigate('/');
        }
        setLoading(false);
    }, 500);
  }

  return (
    <div className='create max-w-2xl mx-auto'>
      <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-slate-100">
        <h2 className="text-3xl font-bold text-slate-800 mb-8 text-left bg-clip-text text-transparent bg-gradient-to-r from-primary to-rose-600">
          {isEditMode ? 'Edit Blog' : 'Add a New Blog'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="group">
            <label className="block text-sm font-semibold text-slate-700 mb-2 group-focus-within:text-primary transition-colors">Blog Title</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-800 placeholder-slate-400"
              placeholder="e.g. The Marvels of React..."
            />
          </div>

          <div className="group">
            <label className="block text-sm font-semibold text-slate-700 mb-2 group-focus-within:text-primary transition-colors">Blog Content</label>
            <textarea
              required
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all min-h-[200px] text-slate-800 placeholder-slate-400 resize-y leading-relaxed"
              placeholder="Share your thoughts..."
            ></textarea>
          </div>

          <div className="group">
            <label className="block text-sm font-semibold text-slate-700 mb-2 group-focus-within:text-primary transition-colors">Author</label>
            <input
              type="text"
              required
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-800 placeholder-slate-400"
              placeholder="Enter your name..."
            />
          </div>

          <div className="pt-4">
              <button 
                  disabled={loading}
                  className={`
                      w-full py-4 rounded-xl font-bold text-white shadow-lg 
                      transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl
                      flex items-center justify-center gap-2
                      ${loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-primary to-rose-600 hover:opacity-90'}
                  `}
              >
                  {loading ? (
                       <span className="flex items-center gap-2">
                           <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                           </svg>
                           Processing...
                       </span>
                  ) : (
                      <>{isEditMode ? 'Update Blog' : 'Publish Blog'}</>
                  )}
              </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Create;
